import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { shipmentReturnService } from "../../services/shipmentReturnService"
import { shipmentService } from "../../services/shipmentService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { Settings, ArrowLeft, RotateCcw } from "lucide-react";
import { PageLayout, Button, FormField, DetailCard } from "../common/index.js";

const LABEL_CLS = "block text-xs font-medium text-secondary mb-1"
const INPUT_CLS =
  "w-full px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
const CARD_CLS = "bg-white rounded-card shadow-card p-3 sm:p-4 mb-4"

const AddReturn = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [shipmentDetails, setShipmentDetails] = useState(null)
  const [returnReasons, setReturnReasons] = useState([])
  const [formData, setFormData] = useState({
    shipment_id: "",
    reason_for_return: "",
  })

  useEffect(() => {
    const fetchReturnReasons = async () => {
      try {
        const response = await shipmentReturnService.getReturnReasons()
        if (response.data.success) {
          setReturnReasons(response.data.data)
        }
      } catch (error) {
        console.error("Error fetching return reasons:", error)
        // Set default reasons if API fails
        setReturnReasons([
          { value: 'wrong_address', label: 'Wrong Address' },
          { value: 'receiver_refused', label: 'Receiver Refused' },
          { value: 'damaged_product', label: 'Damaged Product' },
          { value: 'incomplete_shipment', label: 'Incomplete Shipment' },
          { value: 'delivery_delay', label: 'Delivery Delay' },
          { value: 'customer_request', label: 'Customer Request' },
          { value: 'incorrect_item', label: 'Incorrect Item' },
          { value: 'no_recipient_available', label: 'No Recipient Available' },
          { value: 'other', label: 'Other' }
        ])
      }
    }
    fetchReturnReasons()
  }, [])

  const handleShipmentSearch = async (e) => {
    const shipmentId = e.target.value
    setFormData({ ...formData, shipment_id: shipmentId })
    setShipmentDetails(null)

    if (shipmentId.trim().length === 0) {
      return
    }

    try {
      setSearchLoading(true)
      const response = await shipmentService.getShipment(shipmentId)

      const shipment = response.data.shipment || response.data.data

      if (shipment) {
        // Check if shipment is already returned or closed
        if (
          shipment.status === "Returned" ||
          shipment.status === "RETURNED" ||
          shipment.status === "close" ||
          shipment.status === "closed"
        ) {
          Alert.error("This shipment is already marked as returned or closed")
          setShipmentDetails(null)
          return
        }

        setShipmentDetails(shipment)
      } else {
        setShipmentDetails(null)
        Alert.error("Shipment not found")
      }
    } catch (error) {
      setShipmentDetails(null)
      console.error("Shipment search error:", error)

      if (error.response?.status === 404) {
        Alert.error("Shipment not found")
      } else if (error.response?.data?.error) {
        Alert.error(error.response.data.error)
      } else {
        Alert.error("Error searching for shipment. Please try again.")
      }
    } finally {
      setSearchLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const validateForm = () => {
    if (!formData.shipment_id.trim()) {
      Alert.error("Shipment ID is required")
      return false
    }

    if (!shipmentDetails) {
      Alert.error("Please search and select a valid shipment")
      return false
    }

    if (!formData.reason_for_return) {
      Alert.error("Reason for return is required")
      return false
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const confirmed = await Alert.confirm(
      `Submit return request for shipment ${formData.shipment_id}?`,
      "Confirm Return Request",
    )

    if (!confirmed) return

    try {
      setLoading(true)

      const submitData = {
        shipment_id: formData.shipment_id.trim(),
        reason_for_return: formData.reason_for_return,
      }

      await shipmentReturnService.createReturn(submitData)

      Alert.success("Return request created successfully!")
      navigate("/returns")
    } catch (error) {
      console.error("Error creating return:", error)

      if (error.response?.data?.error) {
        Alert.error(error.response.data.error)
      } else if (error.response?.status === 400) {
        Alert.error("Invalid request. Please check all fields and try again.")
      } else if (error.response?.status === 404) {
        Alert.error("Shipment not found or has already been returned.")
      } else {
        Alert.error("Failed to create return request. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageLayout title="Request Shipment Return" icon={RotateCcw} maxWidth="max-w-7xl">
      <Button
        variant="ghost"
        size="sm"
        icon={ArrowLeft}
        onClick={() => navigate("/returns")}
        className="mb-4 -ml-2"
      >
        Back to Returns
      </Button>
                {/* Shipment Lookup Card */}
                <div className={CARD_CLS}>
                  <div className="w-full mb-4">
                    <h2 className="w-full text-sm font-semibold text-secondary mb-2 pb-2 border-b border-gray-200">
                      Shipment Selection
                    </h2>
                  </div>
                  <div className="mb-4">
                    <label className={LABEL_CLS}>
                      Shipment ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="shipment_id"
                      value={formData.shipment_id}
                      onChange={handleShipmentSearch}
                      placeholder="Enter shipment ID to search (e.g., 123456)"
                      className={`${INPUT_CLS} font-mono`}
                      required
                      disabled={loading}
                    />
                    {searchLoading && (
                      <div className="mt-2 flex items-center gap-2 text-blue-600 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border border-blue-600 border-t-transparent"></div>
                        <span>Searching for shipment...</span>
                      </div>
                    )}
                  </div>

                  {/* Shipment Details Display */}
                  {shipmentDetails && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 p-4 sm:p-5 rounded-card">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="bg-green-500 rounded-full p-1">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-green-800">Shipment Found</h3>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 bg-white p-4 sm:p-5 rounded-xl border border-gray-200">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Shipment ID</p>
                          <p className="text-base sm:text-lg font-bold text-blue-600 font-mono">
                            #{shipmentDetails.shipment_id}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Status</p>
                          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                            {shipmentDetails.status}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Sender Name</p>
                          <p className="text-gray-900 font-semibold">{shipmentDetails.sender_name}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Receiver Name</p>
                          <p className="text-gray-900 font-semibold">{shipmentDetails.receiver_name}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Delivery Address</p>
                          <p className="text-gray-900 text-sm">{shipmentDetails.receiver_address}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Total Amount</p>
                          <p className="text-base sm:text-lg font-bold text-gray-900">
                            ₹{shipmentDetails.total_amount?.toFixed(2) || "0.00"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Payment Mode</p>
                          <p className="text-gray-900 font-semibold">{shipmentDetails.mode_of_payment || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Return Reason Form */}
                <form onSubmit={handleSubmit} className={`${CARD_CLS} overflow-hidden mb-0`}>
                  <div className="mb-3 pb-2 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-secondary">Return Information</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className={LABEL_CLS}>
                        Reason for Return <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="reason_for_return"
                        value={formData.reason_for_return}
                        onChange={handleChange}
                        className={INPUT_CLS}
                        required
                        disabled={loading}
                      >
                        <option value="">Select a reason for return</option>
                        {returnReasons.map((reason) => (
                          <option key={reason.value} value={reason.value}>
                            {reason.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="pt-4 mt-4 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || !shipmentDetails || !formData.reason_for_return}
                      loading={loading}
                    >
                      {loading ? "Creating Request..." : "Create Return Request"}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => navigate("/returns")} disabled={loading}>
                      Cancel
                    </Button>
                  </div>
                </form>
    </PageLayout>
  )
}

export default AddReturn;